import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthGuard } from "@/components/layout/AuthGuard";
import { AdminAuthGuard } from "@/components/layout/AdminAuthGuard";
import { PatternProgressProvider } from "@/contexts/PatternProgressContext";
import { FeedbackButton } from "@/components/layout/FeedbackButton";
import { ScrollToTop } from "@/components/common/ScrollToTop";
import { usePageTracking } from "@/hooks/usePageTracking";
import { Loader2 } from "lucide-react";

// Eager load critical pages
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import Register from "./pages/Register";

// Lazy load less critical pages
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Pricing = lazy(() => import("./pages/Pricing"));

const StitchLibrary = lazy(() => import("./pages/StitchLibrary"));
const ProjectTypes = lazy(() => import("./pages/ProjectTypes"));
const Account = lazy(() => import("./pages/Account"));
const MyKnits = lazy(() => import("./pages/MyKnits"));
const MyShelf = lazy(() => import("./pages/MyShelf"));
const Library = lazy(() => import("./pages/Library"));
const BetaFeedback = lazy(() => import("./pages/BetaFeedback"));
const RavelryGuidePage = lazy(() => import("./pages/RavelryGuidePage"));
const PatternGenerator = lazy(() => import("./pages/PatternGenerator"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const PrintPattern = lazy(() => import("./pages/PrintPattern"));
const ProjectViewer = lazy(() => import("./pages/ProjectViewer"));
const ProjectPrint = lazy(() => import("./pages/ProjectPrint"));

// Lazy load admin pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminStitches = lazy(() => import("./pages/admin/AdminStitches"));
const AdminCastOn = lazy(() => import("./pages/admin/AdminCastOn"));
const AdminBindOff = lazy(() => import("./pages/admin/AdminBindOff"));
const AdminProjectTypes = lazy(() => import("./pages/admin/AdminProjectTypes"));
const AdminStitchSymbols = lazy(() => import("./pages/admin/AdminStitchSymbols"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminFeedback = lazy(() => import("./pages/admin/AdminFeedback"));
const StitchEditor = lazy(() => import("./pages/admin/StitchEditor"));
const StitchViewer = lazy(() => import("./pages/admin/StitchViewer"));
const CastOnEditor = lazy(() => import("./pages/admin/CastOnEditor"));
const BindOffEditor = lazy(() => import("./pages/admin/BindOffEditor"));
const ProjectTypeEditor = lazy(() => import("./pages/admin/ProjectTypeEditor"));
const StitchSymbolEditor = lazy(() => import("./pages/admin/StitchSymbolEditor"));
const StitchSymbolViewer = lazy(() => import("./pages/admin/StitchSymbolViewer"));
const AdminBrandAssets = lazy(() => import("./pages/admin/AdminBrandAssets"));
const AdminTechniqueTutorials = lazy(() => import("./pages/admin/AdminTechniqueTutorials"));
const TechniqueTutorialEditor = lazy(() => import("./pages/admin/TechniqueTutorialEditor"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const BlogPostEditor = lazy(() => import("./pages/admin/BlogPostEditor"));
const AdminPolicies = lazy(() => import("./pages/admin/AdminPolicies"));
const PolicyEditor = lazy(() => import("./pages/admin/PolicyEditor"));

// Public content pages
const About = lazy(() => import("./pages/About"));
const FAQ = lazy(() => import("./pages/FAQ"));
const WhatsNew = lazy(() => import("./pages/WhatsNew"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const LegalPolicy = lazy(() => import("./pages/LegalPolicy"));

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

export default function App() {
  usePageTracking();

  return (
    <>
      <Helmet>
        <title>IntelliStitch - You knit. We calculate.</title>
        <meta name="description" content="Create custom knitting patterns calibrated to your exact gauge and measurements" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      <TooltipProvider>
        <PatternProgressProvider>
          <div className="min-h-screen flex flex-col">
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/stitch-library" element={<StitchLibrary />} />
                <Route path="/project-types" element={<ProjectTypes />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/about" element={<About />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/whats-new" element={<WhatsNew />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />

                {/* Legal Pages — CMS-managed at /legal/:slug */}
                <Route path="/legal/:slug" element={<LegalPolicy />} />
                {/* Redirects from old static URLs */}
                <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
                <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
                <Route path="/cookies" element={<Navigate to="/legal/cookies" replace />} />
                
                <Route path="/account" element={<AuthGuard><Account /></AuthGuard>} />
                <Route path="/studio" element={<AuthGuard><MyKnits /></AuthGuard>} />
                <Route path="/shelf" element={<AuthGuard><MyShelf /></AuthGuard>} />
                <Route path="/library" element={<Library />} />
                <Route path="/beta-feedback" element={<AuthGuard><BetaFeedback /></AuthGuard>} />
                <Route path="/ravelry-guide" element={<RavelryGuidePage />} />
                <Route path="/pattern-generator" element={<AuthGuard><PatternGenerator /></AuthGuard>} />
                <Route path="/projects/:id" element={<AuthGuard><ProjectDetail /></AuthGuard>} />
                
                {/* Admin Routes */}
                <Route path="/admin" element={<AdminAuthGuard><AdminDashboard /></AdminAuthGuard>} />
                <Route path="/admin/stitches" element={<AdminAuthGuard><AdminStitches /></AdminAuthGuard>} />
                <Route path="/admin/stitches/new" element={<AdminAuthGuard><StitchEditor /></AdminAuthGuard>} />
                <Route path="/admin/stitches/edit/:id" element={<AdminAuthGuard><StitchEditor /></AdminAuthGuard>} />
                <Route path="/admin/stitches/view/:id" element={<AdminAuthGuard><StitchViewer /></AdminAuthGuard>} />
                <Route path="/admin/stitch-symbols" element={<AdminAuthGuard><AdminStitchSymbols /></AdminAuthGuard>} />
                <Route path="/admin/stitch-symbols/new" element={<AdminAuthGuard><StitchSymbolEditor /></AdminAuthGuard>} />
                <Route path="/admin/stitch-symbols/edit/:id" element={<AdminAuthGuard><StitchSymbolEditor /></AdminAuthGuard>} />
                <Route path="/admin/stitch-symbols/view/:id" element={<AdminAuthGuard><StitchSymbolViewer /></AdminAuthGuard>} />
                <Route path="/admin/cast-on" element={<AdminAuthGuard><AdminCastOn /></AdminAuthGuard>} />
                <Route path="/admin/cast-on/new" element={<AdminAuthGuard><CastOnEditor /></AdminAuthGuard>} />
                <Route path="/admin/cast-on/edit/:id" element={<AdminAuthGuard><CastOnEditor /></AdminAuthGuard>} />
                <Route path="/admin/bind-off" element={<AdminAuthGuard><AdminBindOff /></AdminAuthGuard>} />
                <Route path="/admin/bind-off/new" element={<AdminAuthGuard><BindOffEditor /></AdminAuthGuard>} />
                <Route path="/admin/bind-off/edit/:id" element={<AdminAuthGuard><BindOffEditor /></AdminAuthGuard>} />
                <Route path="/admin/project-types" element={<AdminAuthGuard><AdminProjectTypes /></AdminAuthGuard>} />
                <Route path="/admin/project-types/new" element={<AdminAuthGuard><ProjectTypeEditor /></AdminAuthGuard>} />
                <Route path="/admin/project-types/edit/:id" element={<AdminAuthGuard><ProjectTypeEditor /></AdminAuthGuard>} />
                <Route path="/admin/users" element={<AdminAuthGuard><AdminUsers /></AdminAuthGuard>} />
                <Route path="/admin/analytics" element={<AdminAuthGuard><AdminAnalytics /></AdminAuthGuard>} />
                <Route path="/admin/settings" element={<AdminAuthGuard><AdminSettings /></AdminAuthGuard>} />
                <Route path="/admin/feedback" element={<AdminAuthGuard><AdminFeedback /></AdminAuthGuard>} />
                <Route path="/admin/brand-assets" element={<AdminAuthGuard><AdminBrandAssets /></AdminAuthGuard>} />
                <Route path="/admin/technique-tutorials" element={<AdminAuthGuard><AdminTechniqueTutorials /></AdminAuthGuard>} />
                <Route path="/admin/technique-tutorials/new" element={<AdminAuthGuard><TechniqueTutorialEditor /></AdminAuthGuard>} />
                <Route path="/admin/technique-tutorials/edit/:id" element={<AdminAuthGuard><TechniqueTutorialEditor /></AdminAuthGuard>} />
                <Route path="/admin/blog" element={<AdminAuthGuard><AdminBlog /></AdminAuthGuard>} />
                <Route path="/admin/blog/new" element={<AdminAuthGuard><BlogPostEditor /></AdminAuthGuard>} />
                <Route path="/admin/blog/edit/:id" element={<AdminAuthGuard><BlogPostEditor /></AdminAuthGuard>} />
                <Route path="/admin/policies" element={<AdminAuthGuard><AdminPolicies /></AdminAuthGuard>} />
                <Route path="/admin/policies/new" element={<AdminAuthGuard><PolicyEditor /></AdminAuthGuard>} />
                <Route path="/admin/policies/edit/:id" element={<AdminAuthGuard><PolicyEditor /></AdminAuthGuard>} />
                
                {/* Print & Project Routes */}
                <Route path="/print/pattern/:patternId" element={<AuthGuard><PrintPattern /></AuthGuard>} />
                <Route path="/project/:projectId" element={<AuthGuard><ProjectViewer /></AuthGuard>} />
                <Route path="/project-print/:projectId" element={<AuthGuard><ProjectPrint /></AuthGuard>} />
                
                {/* 404 Route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </div>
        </PatternProgressProvider>
      </TooltipProvider>

      <FeedbackButton />
      <Toaster />
    </>
  );
}
